import React from 'react';
import ModalResponsive from '@/components/commons/modal-responsive';
import InputSingleFile from '@/components/sections/builder/settings/input-single-file';
import useToast from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import Image from 'next/image';
import { useGetAssets, useGetUnsplash, useUploadImage } from '@/hooks/api/dashboard/file-service';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Pagination from '@/components/commons/pagination';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { useDebounceValue } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { env } from '@/lib/env';

export interface ModalAssetProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  onSelect?: (imageUrl: string) => void;
  initialValue?: string;
}

export default function ModalAsset({
  openModal,
  setOpenModal,
  onSelect,
  initialValue,
}: ModalAssetProps) {
  const { toast } = useToast();
  const [tab, setTab] = React.useState('assets');
  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 50 });
  const [unsplashPagination, setUnsplashPagination] = React.useState({ page: 1, pageSize: 30 });
  const [search, setSearch] = React.useState('');
  const unsplashSearch = useDebounceValue(search, 500);

  const {
    data: assets,
    isLoading: isLoadingAssets,
    refetch: refetchAssets,
  } = useGetAssets(
    { page: pagination.pageIndex + 1, pageSize: pagination.pageSize, q: '' },
    {
      enabled: openModal && tab === 'assets',
    }
  );

  const { data: unsplash, isLoading: isLoadingUnsplash } = useGetUnsplash(
    { page: unsplashPagination.page, pageSize: unsplashPagination.pageSize, q: unsplashSearch },
    {
      enabled: openModal && tab === 'unsplash',
    }
  );

  const handlePreviousPage = React.useCallback(() => {
    if (unsplashPagination.page > 1) {
      setUnsplashPagination((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  }, [unsplashPagination.page]);

  const handleNextPage = React.useCallback(() => {
    setUnsplashPagination((prev) => ({ ...prev, page: prev.page + 1 }));
  }, []);

  const uploadMutation = useUploadImage({
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Gagal upload',
        description: 'Gambar gagal di upload.',
        duration: 1500,
      });
    },
    onSuccess() {
      refetchAssets();
    },
  });

  const handleUploadImage = async (file: Blob) => {
    const data = new FormData();
    data.append('file', file);

    uploadMutation.mutate(data);
  };

  const handleSelectImage = async (downloadLocation: string, imageUrl: string) => {
    onSelect?.(imageUrl);
    const response = await fetch(downloadLocation, {
      method: 'GET',
      headers: {
        Authorization: `Client-ID ${env.NEXT_PUBLIC_UNSPLASH_ACCESS_KEY}`,
      },
    });
    await response.json();
  };

  const skeletonCount = 12;

  return (
    <ModalResponsive openModal={openModal} setOpenModal={setOpenModal}>
      <Tabs defaultValue='assets' value={tab} onValueChange={setTab} className='w-full p-2'>
        <TabsList>
          <TabsTrigger value='assets'>Assets</TabsTrigger>
          <TabsTrigger value='unsplash'>Unsplash</TabsTrigger>
        </TabsList>
        <TabsContent value='assets' className='space-y-4 px-4'>
          <div className=''>
            <InputSingleFile
              name='heroImage'
              type='image'
              onChange={(v) => {
                if (v) handleUploadImage(v.file as Blob);
              }}
              ratio='aspect-[10/1]'
              loading={uploadMutation.isPending}
            />
          </div>
          <ScrollArea className='max-h-[420px] overflow-y-auto pt-0'>
            {isLoadingAssets ? (
              <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                  <div className='flex flex-col space-y-3' key={index}>
                    <Skeleton className='h-[150px] w-full rounded-md' />
                    <div className='space-y-2'>
                      <Skeleton className='h-3 w-full' />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='columns-2 gap-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-5'>
                {assets?.data
                  .filter((item) => item.file_type === 'image')
                  .map((item) => (
                    <div key={item.id} className='mb-4 break-inside-avoid'>
                      <button
                        type='button'
                        onClick={() => onSelect?.(item.file_url || '')}
                        className={cn(
                          'group relative block w-full overflow-hidden transition-all hover:shadow-md',
                          initialValue === item.file_url ? 'border-2 border-primary' : ''
                        )}
                      >
                        <Image
                          className='w-full object-cover transition-transform duration-300 group-hover:scale-105'
                          src={item.file_url}
                          alt={item.display_name}
                          width={300}
                          height={300}
                          placeholder='blur'
                          blurDataURL={`/_next/image?url=${encodeURIComponent(
                            item.file_url || ''
                          )}&w=16&q=1`}
                        />
                        <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100'>
                          {item.original_name}
                        </div>
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </ScrollArea>
          <div className='p-3'>
            <Pagination
              currentPage={pagination.pageIndex + 1}
              totalCount={assets?.totalCount || 0}
              pageSize={pagination.pageSize}
              onPageChange={(page) =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: page - 1,
                }))
              }
              onPageSizeChange={(size) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: size,
                  pageIndex: 0,
                }))
              }
            />
          </div>
        </TabsContent>
        <TabsContent value='unsplash' className='space-y-4 px-4'>
          <div className='mb-4'>
            <Input
              name='search'
              type='text'
              placeholder='Search'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ScrollArea className='max-h-[420px] overflow-y-auto pt-0'>
            {isLoadingUnsplash ? (
              <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                  <div className='flex flex-col space-y-3' key={index}>
                    <Skeleton className='h-[150px] w-full rounded-md' />
                    <div className='space-y-2'>
                      <Skeleton className='h-3 w-full' />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='columns-2 gap-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-5'>
                {unsplash?.data.map((item) => {
                  // Calculate aspect ratio to maintain image proportions
                  const aspectRatio = item.height && item.width ? item.height / item.width : 1;

                  return (
                    <div key={item.id} className='mb-4 break-inside-avoid'>
                      <button
                        type='button'
                        onClick={() =>
                          handleSelectImage(
                            item.links?.download_location || '',
                            item.urls?.regular || ''
                          )
                        }
                        className='group relative w-full overflow-hidden transition-all hover:shadow-md'
                      >
                        <div
                          className='relative'
                          style={{ paddingBottom: `${aspectRatio * 100}%` }}
                        >
                          <Image
                            className='absolute left-0 top-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
                            src={item.urls?.regular || ''}
                            alt={item.description || ''}
                            width={400}
                            height={Math.floor(400 * aspectRatio)}
                            placeholder='blur'
                            blurDataURL={`/_next/image?url=${encodeURIComponent(
                              item.urls?.regular || ''
                            )}&w=16&q=1`}
                          />
                          <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100'>
                            Photo by{' '}
                            <a
                              href={`https://unsplash.com/@${item.user?.username}?utm_source=StoreDashboard&utm_medium=referral&utm_content=${item.id}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='font-medium text-white hover:underline'
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.user?.name}
                            </a>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          <div className='flex items-center justify-end gap-2 p-4'>
            <Button
              variant='outline'
              size='sm'
              onClick={handlePreviousPage}
              disabled={unsplashPagination.page === 1}
            >
              <ChevronLeft className='mr-1 h-4 w-4' />
              Previous
            </Button>
            <Button variant='outline' size='sm' onClick={handleNextPage}>
              Next
              <ChevronRight className='ml-1 h-4 w-4' />
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </ModalResponsive>
  );
}
